import * as THREE from 'three';
import { BLACK, BONE, DSTEEL, DWOOD, WOOD, cloth, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { boxClad, pitchedRoof, seams, type Era2Material } from './era2.ts';
import { ASHLAR, IRON_BAND, IRON_PLATE, IRON_RIVET, IRON_STONE, crack, plate, rubbleAround, snapTop, splinters, stain, type Wear } from './structures.ts';
import { LIGHT_K, begin, done, pointLight, type BaseOpts, type Model } from './types.ts';

/*
 * Class buildings of the new classes: the druid's sacred grove (era II) and the barbarian's clan hall (era III).
 * Unique support buildings without levels, like the workshop and the laboratory: each draws its three
 * materials itself (1 timber and fieldstone, 2 dressed stone, 3 stone bound in iron) and its wear (1 worn,
 * 2 crumbling), and leaves its own ruin.
 */

export interface ClassBuildingOpts extends BaseOpts {
  material?: Era2Material;
  wear?: Wear;
}

const TAU = Math.PI * 2;
const EARTH = std('#3a3226', { roughness: 1, flatShading: true });
const TURF = std('#3f5530', { roughness: 1, bumpScale: 0.04 });
const MOSS = std('#5d7a3a', { roughness: 1, bumpScale: 0.04 });
const MOSS_DK = std('#3f5a2e', { roughness: 1, bumpScale: 0.04 });
const BARK = std('#3e3128', { roughness: 1, bumpScale: 0.04 });
const LEAVES = [std('#2f4a2a', { roughness: 0.95, flatShading: true }), std('#3d5a30', { roughness: 0.95, flatShading: true }), std('#263d22', { roughness: 0.95, flatShading: true })];
const DEAD = std('#4a3f30', { roughness: 1, flatShading: true });
const PLINTH = stone('#5f5b55');
const FIRE = emis('#ffb070', '#ff5a10', 1.3);

const box = (parent: THREE.Object3D, mat: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
  const m = M(new THREE.BoxGeometry(w, h, d), mat, parent);
  m.position.set(x, y, z);
  m.rotation.y = ry;
  return m;
};

const stoneOf = (mat: Era2Material): THREE.MeshStandardMaterial => (mat === 1 ? stone('#77736c') : stone(mat === 3 ? IRON_STONE : '#8f8a80'));
const rubbleColors = (mat: Era2Material): string[] => (mat === 1 ? ['#77736c', '#6b675f', '#837e75'] : mat === 3 ? ['#72767c', '#5d6167', '#868a90'] : ASHLAR);

/* ---------- the druid's grove ---------- */

/** An old gnarled tree on its roots, its canopy in clumps; `bare` takes clumps away (worn), `snapped` breaks it. */
const oldTree = (parent: THREE.Object3D, x: number, z: number, h: number, ry: number, rng: () => number, bare = 0, snapped = false): void => {
  const tg = G(parent, x, 0, z);
  tg.rotation.y = ry;
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * TAU + rng() * 0.5;
    limb(tg, V(0, 0.13, 0), V(Math.cos(a) * 0.2, -0.005, Math.sin(a) * 0.2), 0.045, 0.016, BARK, 5);
  }
  const p1 = V(0.06, h * 0.42, 0.02);
  const p2 = V(-0.02, h * 0.78, -0.02);
  limb(tg, V(0, 0, 0), p1, 0.09, 0.066, BARK, 7);
  const knot = M(new THREE.DodecahedronGeometry(0.07, 0), MOSS, tg);
  knot.position.set(0.05, 0.1, 0.06);
  knot.scale.set(1, 0.6, 0.8);
  if (snapped) {
    // Broken at the fork: the jagged stump stands, the crown lies on the ground beside it.
    snapTop(tg, p1.x, p1.y, p1.z, 0.066, BARK, rng);
    const fall = G(tg, 0.15, 0.06, 0);
    fall.rotation.set(0, 0.4, -Math.PI / 2 + 0.12);
    limb(fall, V(0, 0, 0), V(0, h * 0.5, 0), 0.06, 0.04, BARK, 6);
    for (const [cx, cy, cz, r] of [[0.02, h * 0.55, 0, 0.18], [-0.1, h * 0.45, 0.08, 0.14]] as const) {
      const cl = M(new THREE.IcosahedronGeometry(r, 0), DEAD, fall);
      cl.position.set(cx, cy, cz);
      cl.scale.set(1, 0.75, 1);
    }
    return;
  }
  limb(tg, p1, p2, 0.066, 0.045, BARK, 7);
  const b1 = V(-0.2, h * 0.7, 0.06), b2 = V(0.18, h * 0.86, -0.05);
  limb(tg, p1.clone().lerp(p2, 0.3), b1, 0.035, 0.016, BARK, 5);
  limb(tg, p2, b2, 0.03, 0.014, BARK, 5);
  const clumps: [number, number, number, number][] = [
    [p2.x, h, p2.z, 0.22], [b1.x, b1.y + 0.06, b1.z, 0.17], [b2.x, b2.y + 0.05, b2.z, 0.17], [0.04, h * 0.9, 0.16, 0.15], [-0.06, h * 0.95, -0.16, 0.15],
  ];
  clumps.forEach(([cx, cy, cz, r], i) => {
    if (i > 0 && i <= bare) return;
    const cl = M(new THREE.IcosahedronGeometry(r, 0), LEAVES[i % 3] as THREE.Material, tg);
    cl.position.set(cx + (rng() - 0.5) * 0.04, cy, cz + (rng() - 0.5) * 0.04);
    cl.rotation.set(rng() * 3, rng() * 3, 0);
    cl.scale.y = 0.82;
  });
  if (bare > 0) limb(tg, b1, b1.clone().add(V(-0.12, 0.12, 0.02)), 0.014, 0.006, DEAD, 4);
};

/** A standing stone `h` tall: rough fieldstone (1), dressed (2) or dressed and iron-bound (3). */
const standing = (parent: THREE.Object3D, mat: Era2Material, x: number, z: number, h: number, ry: number): THREE.Group => {
  const sg = G(parent, x, 0, z);
  sg.rotation.y = ry;
  const st = stoneOf(mat);
  if (mat === 1) {
    const r = M(new THREE.DodecahedronGeometry(0.09, 0), st, sg);
    r.position.y = h / 2;
    r.scale.set(0.85, h / 0.17, 0.6);
  } else {
    const r = M(new THREE.CylinderGeometry(0.052, 0.072, h, 4), st, sg);
    r.position.y = h / 2;
    r.rotation.y = Math.PI / 4;
    if (mat === 3) {
      for (const y of [h * 0.3, h * 0.72]) {
        const w = 0.074 - (y / h) * 0.02;
        const band = M(new THREE.CylinderGeometry(w * 1.04, w * 1.04, 0.035, 4, 1, true), IRON_BAND, sg);
        band.position.y = y;
        band.rotation.y = Math.PI / 4;
        M(new THREE.OctahedronGeometry(0.011, 0), IRON_RIVET, sg).position.set(0, y, w * 0.74);
      }
      const cap = M(new THREE.ConeGeometry(0.06, 0.06, 4), IRON_PLATE, sg);
      cap.position.y = h + 0.03;
      cap.rotation.y = Math.PI / 4;
    }
  }
  const tuft = M(new THREE.DodecahedronGeometry(0.045, 0), MOSS, sg);
  tuft.position.set(0.02, h * (mat === 1 ? 0.95 : 0.98), 0.01);
  tuft.scale.y = 0.45;
  return sg;
};

/**
 * The sacred grove: a mossy mound ringed by three old trees and standing stones, a gate of two stones and a
 * lintel at the front (a timber lintel hung with charms in wood), roots crawling to a carved stone at the
 * center whose spiral glows faintly green. Iron binds the stones and caps them in material 3. Worn (1):
 * cracked stones, a tree going bare, damp stains. Crumbling (2): a stone toppled, a tree snapped at the fork,
 * the lintel fallen and the carving nearly dark.
 */
export const grove = (o?: ClassBuildingOpts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.8, 95);
  const { g } = c;
  const wr = mulberry32(950 + mat * 3 + wear);
  const st = stoneOf(mat);
  const Y0 = 0.1;
  M(new THREE.CylinderGeometry(0.6, 0.66, 0.08, 14), EARTH, g).position.y = 0.04;
  M(new THREE.CylinderGeometry(0.57, 0.6, 0.02, 14), TURF, g).position.y = 0.09;
  if (mat === 3) ring(g, 0.6, 0.016, IRON_BAND, 0.085, 3, 28);
  const top = G(g, 0, Y0, 0);

  // The carved stone at the center, a flat offering stone before it.
  const glyphB = [0.9, 0.55, 0.2][wear] as number;
  const glyph = emis('#d8f5c0', '#6fa05a', glyphB, { flatShading: true });
  let face = 0.09;
  if (mat === 1) {
    const r = M(new THREE.DodecahedronGeometry(0.13, 0), st, top);
    r.position.y = 0.26;
    r.scale.set(0.9, 2.2, 0.7);
  } else {
    const r = M(new THREE.CylinderGeometry(0.075, 0.11, 0.5, 4), st, top);
    r.position.y = 0.25;
    r.rotation.y = Math.PI / 4;
    const cap = M(new THREE.CylinderGeometry(0.03, 0.08, 0.08, 4), mat === 3 ? IRON_PLATE : st, top);
    cap.position.y = 0.54;
    cap.rotation.y = Math.PI / 4;
    face = 0.068;
    if (mat === 3) {
      for (const y of [0.08, 0.45]) {
        const w = 0.11 - (y / 0.5) * 0.035;
        const band = M(new THREE.CylinderGeometry(w * 1.05, w * 1.05, 0.035, 4, 1, true), IRON_BAND, top);
        band.position.y = y;
        band.rotation.y = Math.PI / 4;
      }
    }
  }
  // The spiral: two rings and a dot on the face toward the field (crumbling: split by a crack).
  const sp = G(top, 0, 0.3, face + 0.004);
  M(new THREE.TorusGeometry(0.05, 0.008, 3, 14), glyph, sp);
  M(new THREE.TorusGeometry(0.026, 0.008, 3, 10, Math.PI * 1.5), glyph, sp);
  M(new THREE.CircleGeometry(0.01, 6), glyph, sp).position.z = 0.002;
  glowSprite(top, '#9fe07a', 0.45, V(0, 0.3, face + 0.05), [0.5, 0.32, 0.12][wear] as number);
  if (wear > 0) crack(top, 0.02, 0.45, face + 0.008, 0, wear === 2 ? 0.34 : 0.22, wr);
  const off = M(new THREE.CylinderGeometry(0.1, 0.11, 0.05, 7), st, top);
  off.position.set(0, 0.025, 0.2);
  off.scale.z = 0.7;
  M(new THREE.DodecahedronGeometry(0.03, 0), MOSS, top).position.set(-0.04, 0.06, 0.2);

  // Roots crawling from the trees toward the stone.
  const trees: [number, number, number][] = [[-0.33, -0.3, 1.22], [0.34, -0.3, 1.08], [-0.45, 0.1, 0.95]];
  for (const [tx, tz] of trees) {
    const pts = [V(tx * 0.9, 0.0, tz * 0.9), V(tx * 0.6 + 0.04, 0.012, tz * 0.6), V(tx * 0.32, 0.0, tz * 0.32 + 0.03), V(tx * 0.18, 0.005, tz * 0.18)];
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 8, 0.02, 4), BARK, top);
  }
  trees.forEach(([tx, tz, h], i) => oldTree(top, tx, tz, h, i * 2.1 + 0.4, wr, wear > 0 && i === 1 ? 2 : 0, wear === 2 && i === 2));

  // Standing stones on the open side, and the gate at the front.
  const right = standing(top, mat, 0.47, 0.02, 0.42, Math.PI / 2);
  if (wear === 2) {
    // Toppled outward, half sunk in the turf.
    right.rotation.set(0, 0, -(Math.PI / 2 - 0.08));
    right.position.set(0.4, 0.06, 0.05);
  }
  standing(top, mat, 0.33, -0.02 - 0.42, 0.34, 0.6);
  const gx = 0.17, gz = 0.44, gh = 0.48;
  for (const k of [-1, 1]) standing(top, mat, k * gx, gz, gh, 0);
  const lintel = G(top, 0, gh + 0.035, gz);
  if (wear === 2) {
    lintel.position.set(0.08, 0.03, gz + 0.18);
    lintel.rotation.set(0, 0.5, 0.06);
  }
  if (mat === 1) {
    limb(lintel, V(-0.25, 0, 0), V(0.25, 0.01, 0), 0.04, 0.035, DWOOD, 6);
    // Charms hung from the timber: a bundle of bones and a moss knot.
    if (wear < 2) {
      for (const [x, m] of [[-0.08, BONE], [0.06, MOSS], [0.12, BONE]] as const) {
        limb(lintel, V(x, 0, 0), V(x, -0.1, 0), 0.004, 0.004, BARK, 3);
        M(new THREE.DodecahedronGeometry(0.022, 0), m, lintel).position.set(x, -0.12, 0);
      }
    }
  } else {
    box(lintel, st, 0.5, 0.075, 0.1, 0, 0, 0);
    if (mat === 3) for (const x of [-0.17, 0.17]) box(lintel, IRON_BAND, 0.035, 0.085, 0.112, x, 0, 0);
    M(new THREE.DodecahedronGeometry(0.04, 0), MOSS, lintel).position.set(-0.12, 0.04, 0);
  }
  if (wear === 2) snapTop(top, 0.47, 0.02, 0.02, 0.05, st, wr);

  // Moss and ferns in the turf.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * TAU + wr() * 0.4;
    const d = 0.2 + wr() * 0.3;
    const m = M(new THREE.DodecahedronGeometry(0.035 + wr() * 0.03, 0), i % 2 ? MOSS : MOSS_DK, top);
    m.position.set(Math.sin(a) * d, 0.01, Math.cos(a) * d);
    m.scale.y = 0.45;
  }

  if (wear > 0) {
    for (const [x, z, ry] of [[-0.17, gz + 0.06, 0], [0.17, gz + 0.06, 0], [0.33, -0.36, 0.6]] as const) {
      if (wr() < 0.5 + 0.3 * wear) crack(top, x, 0.38, z, ry, wear === 2 ? 0.26 : 0.18, wr);
    }
    stain(top, -0.17, 0.2, gz + 0.065, 0, 0.05, wr, '#2a2d22');
    stain(top, 0.1, 0.18, face + 0.008, 0, 0.05, wr, '#2a2d22');
    rubbleAround(g, wear === 2 ? 9 : 4, 0.55, wr, rubbleColors(mat));
  }
  const L = pointLight(c, 0x9fe07a, [0.2, 0.12, 0.05][wear] as number, 1.4, V(0, Y0 + 0.35, 0.25));
  const tick = (n: number): void => {
    L.intensity = ([0.2, 0.12, 0.05][wear] as number) * (1 + (n % 7 < 3 ? 0.15 : 0)) * LIGHT_K;
  };
  return done(c, 1.5, tick);
};

/* ---------- the barbarian's clan hall ---------- */

const SHIELD_PAINT = [cloth('#8e2a22'), cloth('#2b2d31'), cloth('#6d737c'), cloth('#5e1c1c')];

/** A round shield hung flat on a wall, face toward +Z of `parent`, with an iron rim and boss and a painted stripe. */
export const roundShield = (parent: THREE.Object3D, x: number, y: number, z: number, ry: number, i: number, r = 0.075): THREE.Group => {
  const sg = G(parent, x, y, z);
  sg.rotation.y = ry;
  const disc = M(new THREE.CylinderGeometry(r, r, 0.016, 10), SHIELD_PAINT[i % 4] as THREE.Material, sg);
  disc.rotation.x = Math.PI / 2;
  const rim = M(new THREE.TorusGeometry(r, 0.008, 3, 12), DSTEEL, sg);
  rim.position.z = 0.002;
  M(new THREE.SphereGeometry(0.022, 6, 3, 0, TAU, 0, Math.PI / 2), DSTEEL, sg).rotation.x = Math.PI / 2;
  if (i % 2 === 0) box(sg, SHIELD_PAINT[(i + 1) % 4] as THREE.Material, r * 1.9, 0.024, 0.004, 0, 0, 0.009).rotation.z = 0.6 + i;
  return sg;
};

/** A horned skull, facing +Z; `s` its size. */
export const hornedSkull = (parent: THREE.Object3D, x: number, y: number, z: number, s: number, long = false): void => {
  const sk = G(parent, x, y, z);
  sk.scale.setScalar(s);
  const cr = M(new THREE.SphereGeometry(0.06, 8, 6), BONE, sk);
  cr.scale.set(0.95, 0.9, 0.9);
  box(sk, BONE, 0.05, 0.045, 0.07, 0, -0.035, 0.04);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.014, 5, 3), BLACK, sk).position.set(k * 0.022, 0.0, 0.05);
    const pts = long
      ? [V(k * 0.05, 0.02, 0), V(k * 0.13, 0.04, 0.01), V(k * 0.18, 0.11, 0.03), V(k * 0.16, 0.18, 0.05)]
      : [V(k * 0.05, 0.03, 0), V(k * 0.1, 0.07, 0.01), V(k * 0.11, 0.13, 0.03)];
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 6, 0.014, 4), std('#c9bfa8', { roughness: 0.8 }), sk);
  }
};

/** A war drum on three short legs, red-banded, its sticks across the head; `fallen` tips it on its side. */
const drum = (parent: THREE.Object3D, x: number, z: number, fallen: boolean): void => {
  const dg = G(parent, x, 0, z);
  if (fallen) {
    dg.position.set(x + 0.06, 0.07, z + 0.05);
    dg.rotation.set(0.3, 0.4, Math.PI / 2);
  } else {
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * TAU + 0.5;
      limb(dg, V(Math.cos(a) * 0.04, 0.09, Math.sin(a) * 0.04), V(Math.cos(a) * 0.07, 0, Math.sin(a) * 0.07), 0.008, 0.008, DWOOD, 4);
    }
    for (const k of [-1, 1]) limb(dg, V(k * 0.05, 0.245, -0.03), V(-k * 0.03, 0.25, 0.07), 0.006, 0.006, DWOOD, 4);
  }
  const y0 = fallen ? 0 : 0.17;
  M(new THREE.CylinderGeometry(0.075, 0.068, 0.15, 10), WOOD, dg).position.y = y0;
  M(new THREE.CylinderGeometry(0.077, 0.077, 0.012, 10), leather('#7d6a58'), dg).position.y = y0 + 0.078;
  ring(dg, 0.074, 0.008, cloth('#8e1f22'), y0 + 0.03, 3, 12);
  ring(dg, 0.071, 0.006, DSTEEL, y0 - 0.05, 3, 12);
};

/**
 * The clan hall: a long, low mead-hall under a steep roof whose crossed gable boards rise past the ridge into
 * carved beast heads, like a ship's prows. Round shields hang along its walls, horned skulls of fallen
 * warlords over the door, firelight in the gap of the doors and two war drums at the threshold. Timber staves
 * on fieldstone in wood, a dressed stone footing in stone, plated, banded and iron-roofed in iron. Worn (1):
 * stains and cracks, a shield fallen, gaps in the roof. Crumbling (2): a prow snapped (its head on the ground),
 * the roof holed, a drum knocked over, the banner torn.
 */
export const clanHall = (o?: ClassBuildingOpts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.85, 96);
  const { g } = c;
  const wr = mulberry32(960 + mat * 3 + wear);
  const iron = mat === 3;
  const timber = std('#5b4330', { roughness: 1 });
  const footing = stoneOf(mat);
  const W = 1.04, D = 0.54, Z = -0.08, y0 = 0.08, H = 0.38;
  const front = Z + D / 2;
  box(g, PLINTH, 1.3, 0.08, 0.92, 0, 0.04, 0);
  if (mat === 1) {
    box(g, timber, W, H, D, 0, y0 + H / 2, Z);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, DWOOD, 0.05, H, 0.05, (sx * W) / 2, y0 + H / 2, Z + (sz * D) / 2);
    for (let i = 0; i < 6; i++) {
      const m = M(new THREE.DodecahedronGeometry(0.035 + wr() * 0.02, 0), footing, g);
      m.position.set(-W / 2 + 0.08 + wr() * (W - 0.16), y0 + 0.02, front + 0.01);
      m.scale.z = 0.5;
    }
  } else {
    box(g, footing, W + 0.03, 0.14, D + 0.03, 0, y0 + 0.07, Z);
    box(g, timber, W, H - 0.14, D, 0, y0 + 0.14 + (H - 0.14) / 2, Z);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, DWOOD, 0.05, H - 0.14, 0.05, (sx * W) / 2, y0 + 0.14 + (H - 0.14) / 2, Z + (sz * D) / 2);
  }
  // Staves on the long walls.
  const stave0 = mat === 1 ? y0 : y0 + 0.14;
  for (let x = -W / 2 + 0.07; x < W / 2 - 0.04; x += 0.085) {
    if (Math.abs(x) < 0.16) continue;
    for (const s of [1, -1]) box(g, DWOOD, 0.012, y0 + H - stave0, 0.008, x, (stave0 + y0 + H) / 2, Z + (s * D) / 2 + s * 0.004);
  }
  if (iron) {
    boxClad(g, 0, Z, W, D, y0, y0 + H, [y0 + 0.14, y0 + H - 0.03], y0 + 0.14, ['l', 'b', 'r']);
    for (const x of [-0.36, 0.36]) plate(g, x, y0 + 0.07, front + 0.024, 0.2, 0.12, 0);
  }

  // The steep roof, ridge along the hall.
  const roofM = mat === 1 ? std('#3a271a', { roughness: 0.95, flatShading: true }) : iron ? metal('#3d434b', { roughness: 0.42, metalness: 0.6, flatShading: true }) : std('#3b3128', { roughness: 0.95, flatShading: true });
  const span = D + 0.06, rh = 0.38, len = W + 0.06;
  const roof = pitchedRoof(g, 0, y0 + H, Z, span, rh, len, roofM, timber, false, 0.07, 0.04);
  if (iron) seams(roof.grp, span, rh, len + 0.06, roof.angle, 0.13);
  const slope = roof.angle;
  if (wear > 0) {
    for (let i = 0; i < wear * 2 + 1; i++) {
      const side = i % 2 ? -1 : 1;
      const k = 0.3 + wr() * 0.45;
      const gap = box(roof.grp, std('#140f0c', { roughness: 1 }), 0.13 + wr() * 0.08, 0.012, 0.05, (wr() - 0.5) * 0.8, rh * (1 - k) + 0.03, side * (span / 2) * k);
      gap.rotation.x = side * slope;
    }
  }
  if (wear === 2) {
    const hole = box(roof.grp, std('#120e0b', { roughness: 1 }), 0.3, 0.012, 0.2, -0.22, rh * 0.5 + 0.035, span / 4 + 0.01);
    hole.rotation.x = slope;
    for (const x of [-0.3, -0.14]) {
      const raf = box(roof.grp, std('#2b2119', { roughness: 1 }), 0.03, 0.03, 0.26, x, rh * 0.5 + 0.04, span / 4);
      raf.rotation.x = slope;
    }
  }

  // Crossed gable boards rising past the ridge into carved heads (crumbling: one prow snapped off).
  const carve = DWOOD;
  const red = cloth('#8e1f22');
  const half = span / 2 + 0.1;
  for (const sx of [-1, 1]) {
    const pg = G(roof.grp, sx * (len / 2 + 0.035), 0, 0);
    for (const s of [-1, 1]) {
      const eave = V(0, -0.03, s * half);
      const dir = V(0, rh + 0.03, -s * half).normalize();
      const broken = wear === 2 && sx > 0 && s > 0;
      const ext = broken ? 0.06 : 0.3;
      const end = V(0, rh + 0.02, 0).addScaledVector(dir, ext);
      limb(pg, eave, end, 0.03, 0.026, carve, 4);
      if (broken) {
        snapTop(pg, end.x, end.y - 0.02, end.z, 0.02, carve, wr);
        continue;
      }
      // The head: a snout curling outward with a red eye and a jaw.
      const hd = G(pg, end.x, end.y, end.z);
      hd.rotation.x = s * (Math.PI / 2 - slope) * 0.9;
      hd.scale.setScalar(1.6);
      box(hd, carve, 0.04, 0.05, 0.1, 0, 0.01, -s * 0.03);
      const jaw = M(new THREE.ConeGeometry(0.022, 0.08, 4), carve, hd);
      jaw.position.set(0, -0.015, -s * 0.09);
      jaw.rotation.x = -s * Math.PI / 2;
      for (const k of [-1, 1]) box(hd, red, 0.006, 0.014, 0.014, k * 0.021, 0.02, -s * 0.05);
      const curl = M(new THREE.TorusGeometry(0.03, 0.01, 3, 8, Math.PI * 1.3), carve, hd);
      curl.position.set(0, 0.05, s * 0.02);
      curl.rotation.y = Math.PI / 2;
    }
  }
  if (wear === 2) {
    // The snapped head lies by the wall.
    const fh = G(g, len / 2 + 0.16, 0.1, Z + 0.22);
    fh.rotation.set(0.2, 0.9, Math.PI / 2 - 0.2);
    box(fh, carve, 0.04, 0.05, 0.1, 0, 0, 0);
    limb(fh, V(0, 0, 0.05), V(0, 0, 0.2), 0.02, 0.02, carve, 4);
  }

  // The doors, a firelit gap between them, carved posts and the warlords' skulls above.
  const door = G(g, 0, y0, front + 0.006);
  box(door, BLACK, 0.24, 0.27, 0.01, 0, 0.135, 0);
  for (const k of [-1, 1]) {
    const leaf = box(door, DWOOD, 0.1, 0.25, 0.018, k * 0.056, 0.125, 0.008);
    leaf.rotation.y = k * (wear === 2 ? 0.35 : 0.08);
    for (const y of [0.06, 0.19]) box(leaf, iron ? IRON_BAND : DSTEEL, 0.1, 0.016, 0.008, 0, y - 0.125, 0.012);
    box(door, carve, 0.04, 0.33, 0.05, k * 0.14, 0.165, 0.015);
    const knob = M(new THREE.ConeGeometry(0.025, 0.06, 4), carve, door);
    knob.position.set(k * 0.14, 0.36, 0.015);
  }
  box(door, carve, 0.33, 0.04, 0.05, 0, 0.3, 0.015);
  box(door, FIRE, 0.012, 0.2, 0.004, 0, 0.11, 0.004);
  glowSprite(door, '#ff7a2a', 0.42, V(0, 0.12, 0.06), [0.75, 0.6, 0.45][wear] as number);
  hornedSkull(door, 0, 0.4, 0.04, 1.25, true);
  for (const k of [-1, 1]) hornedSkull(door, k * 0.14, 0.25, 0.05, 0.75);

  // Shields along the walls (worn: one fallen at the foot; crumbling: two, one split).
  const spots: [number, number, number, number][] = [[-0.43, front + 0.012, 0, 0], [-0.28, front + 0.012, 0, 1], [0.28, front + 0.012, 0, 2], [0.43, front + 0.012, 0, 3], [-W / 2 - 0.012, Z, -Math.PI / 2, 1], [W / 2 + 0.012, Z, Math.PI / 2, 2]];
  spots.forEach(([x, z, ry, i], n) => {
    const down = (wear >= 1 && n === 2) || (wear === 2 && n === 0);
    if (down) {
      const sg = roundShield(g, x + 0.03, 0.09, z + 0.12, 0, i);
      sg.rotation.set(-Math.PI / 2 + 0.25, 0, 0.6);
      return;
    }
    roundShield(g, x, y0 + 0.24, z, ry, i);
  });

  // War drums at the threshold.
  drum(g, -0.2, front + 0.15, false);
  drum(g, 0.2, front + 0.15, wear === 2);

  // A war banner at the end of the hall.
  const flag = G(g, -0.58, 0.08, front + 0.06);
  if (wear === 2) flag.rotation.set(0.1, 0, 0.22);
  limb(flag, V(0, 0, 0), V(0, 1.05, 0), 0.014, 0.012, DWOOD, 5);
  box(flag, DWOOD, 0.26, 0.02, 0.02, 0.1, 0.98, 0);
  box(flag, cloth('#7a1d22'), 0.2, wear === 2 ? 0.2 : 0.36, 0.01, 0.12, wear === 2 ? 0.87 : 0.79, 0);
  if (wear < 2) {
    for (const x of [0.06, 0.12, 0.18]) {
      const tip = M(new THREE.ConeGeometry(0.02, 0.07, 3), cloth('#7a1d22'), flag);
      tip.position.set(x, 0.575, 0);
      tip.rotation.x = Math.PI;
    }
  }
  hornedSkull(flag, 0, 1.08, 0, 0.6);

  if (wear > 0) {
    for (const [x, z, ry] of [[-0.3, front + 0.003, 0], [0.38, front + 0.003, 0], [W / 2 + 0.003, Z + 0.1, Math.PI / 2], [-W / 2 - 0.003, Z - 0.1, -Math.PI / 2]] as const) {
      if (mat > 1) crack(g, x, y0 + 0.12, z + (z === front + 0.003 ? 0.014 : 0), ry, wear === 2 ? 0.2 : 0.14, wr);
      stain(g, x + 0.05, y0 + 0.26, z, ry, 0.06 + wr() * 0.04, wr, '#2e241b');
    }
    splinters(g, wear === 2 ? 7 : 3, 0.62, wr, 0, Z);
    if (mat > 1) rubbleAround(g, wear === 2 ? 8 : 4, 0.62, wr, rubbleColors(mat), 0, Z);
  }
  const L = pointLight(c, 0xff8a3a, [0.35, 0.28, 0.2][wear] as number, 1.6, V(0, y0 + 0.15, front + 0.25));
  const tick = (n: number): void => {
    L.intensity = ([0.35, 0.28, 0.2][wear] as number) * (1 + (n % 5 < 2 ? 0.18 : 0) - (n % 7 === 3 ? 0.1 : 0)) * LIGHT_K;
  };
  return done(c, 1.32, tick);
};

/* ---------- ruins ---------- */

/** What a destroyed grove or clan hall leaves, in its material. */
export const classRuin = (type: string, mat: Era2Material = 2): Model => {
  const c = begin(undefined, 0.7, 97);
  const { g } = c;
  const rng = mulberry32(970 + type.length * 13 + mat * 5);
  const st = stoneOf(mat);
  const colors = rubbleColors(mat);
  const scorch = (r: number, x = 0, z = 0, sx = 1): void => {
    const d = M(new THREE.CircleGeometry(r, 12), std('#1e1a16', { roughness: 1, bumpScale: 0 }), g);
    d.rotation.x = -Math.PI / 2;
    d.scale.x = sx;
    d.position.set(x, 0.006, z);
  };
  const ironBits = (r: number): void => {
    if (mat !== 3) return;
    for (let i = 0; i < 5; i++) {
      const a = rng() * TAU;
      const p = box(g, IRON_PLATE, 0.12 + rng() * 0.08, 0.012, 0.09 + rng() * 0.05, Math.sin(a) * r * (0.6 + rng() * 0.4), 0.02 + rng() * 0.03, Math.cos(a) * r * (0.6 + rng() * 0.4), rng() * 3);
      p.rotation.x = (rng() - 0.5) * 0.6;
    }
    const band = M(new THREE.TorusGeometry(0.16, 0.014, 4, 12, Math.PI * 0.9), IRON_BAND, g);
    band.position.set(r * 0.3, 0.03, -r * 0.4);
    band.rotation.set(-Math.PI / 2 + 0.15, 0, rng() * 3);
  };
  let h: number;
  if (type === 'grove') {
    M(new THREE.CylinderGeometry(0.58, 0.64, 0.06, 14), EARTH, g).position.y = 0.03;
    scorch(0.42, 0, 0, 1.1);
    // Three stumps snapped low, a dead crown fallen across the ring.
    for (const [x, z, sh] of [[-0.33, -0.3, 0.24], [0.34, -0.3, 0.16], [-0.45, 0.1, 0.2]] as const) {
      limb(g, V(x, 0.04, z), V(x + 0.02, sh, z), 0.09, 0.07, BARK, 7);
      snapTop(g, x + 0.02, sh, z, 0.07, BARK, rng);
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * TAU + rng();
        limb(g, V(x, 0.1, z), V(x + Math.cos(a) * 0.18, 0.02, z + Math.sin(a) * 0.18), 0.04, 0.015, BARK, 5);
      }
    }
    const crown = G(g, 0.1, 0.07, 0.25);
    crown.rotation.set(0, 0.8, Math.PI / 2 - 0.1);
    limb(crown, V(0, -0.2, 0), V(0, 0.45, 0), 0.05, 0.03, BARK, 6);
    M(new THREE.IcosahedronGeometry(0.15, 0), DEAD, crown).position.set(0.02, 0.45, 0);
    // The carved stone broken in two, the standing stones toppled.
    const base = mat === 1 ? M(new THREE.DodecahedronGeometry(0.12, 0), st, g) : M(new THREE.CylinderGeometry(0.1, 0.11, 0.18, 4), st, g);
    base.position.y = 0.1;
    if (mat === 1) base.scale.set(0.9, 0.9, 0.7);
    else base.rotation.y = Math.PI / 4;
    const half2 = mat === 1 ? M(new THREE.DodecahedronGeometry(0.12, 0), st, g) : M(new THREE.CylinderGeometry(0.075, 0.1, 0.3, 4), st, g);
    half2.position.set(0.22, 0.09, 0.12);
    half2.rotation.set(Math.PI / 2 - 0.1, 0.7, 0);
    for (const [x, z, ry] of [[0.47, 0.05, 1.2], [-0.15, 0.48, 0.3], [0.2, 0.47, -0.4]] as const) {
      const s = mat === 1 ? M(new THREE.DodecahedronGeometry(0.09, 0), st, g) : M(new THREE.CylinderGeometry(0.052, 0.072, 0.4, 4), st, g);
      s.position.set(x, 0.07, z);
      s.rotation.set(Math.PI / 2 - 0.1, ry, 0);
      if (mat === 1) s.scale.set(0.85, 2.2, 0.6);
    }
    rubbleAround(g, 8, 0.45, rng, colors);
    h = 0.35;
  } else {
    // The hall: its footing and stave stumps, charred roof timbers, a fallen prow head, a drum and shields.
    scorch(0.55, 0, -0.08, 1.35);
    const W = 1.04, D = 0.54, Z = -0.08;
    for (const [x, z, ww, dd] of [[0, Z + D / 2, W, 0.06], [0, Z - D / 2, W, 0.06], [-W / 2, Z, 0.06, D], [W / 2, Z, 0.06, D]] as const) {
      const n = Math.max(2, Math.round(Math.max(ww, dd) / 0.18));
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n - 0.5;
        const sh = 0.05 + rng() * (mat === 1 ? 0.18 : 0.12);
        box(g, mat === 1 ? std('#2b2119', { roughness: 1 }) : st, ww > dd ? ww / n - 0.012 : ww, sh, dd > ww ? dd / n - 0.012 : dd, x + (ww > dd ? t * ww : 0), sh / 2, z + (dd > ww ? t * dd : 0));
      }
    }
    for (let i = 0; i < 4; i++) {
      const raf = box(g, std('#2b2119', { roughness: 1 }), 0.04, 0.04, 0.5 + rng() * 0.3, -0.35 + i * 0.24, 0.05 + (i % 2) * 0.03, Z + (rng() - 0.5) * 0.2, 0.3 + rng() * 0.6);
      raf.rotation.z = (rng() - 0.5) * 0.3;
    }
    const fh = G(g, 0.62, 0.05, 0.25);
    fh.rotation.set(0, 0.6, Math.PI / 2);
    box(fh, DWOOD, 0.04, 0.05, 0.1, 0, 0, 0);
    limb(fh, V(0, 0, 0.05), V(0, 0, 0.3), 0.02, 0.02, DWOOD, 4);
    drum(g, -0.45, 0.35, true);
    const sh1 = roundShield(g, 0.25, 0.02, 0.38, 0, 0);
    sh1.rotation.set(-Math.PI / 2 + 0.1, 0, 0.5);
    const sh2 = roundShield(g, -0.1, 0.025, 0.4, 0, 3);
    sh2.rotation.set(-Math.PI / 2 + 0.25, 0, 2.1);
    hornedSkull(g, 0.05, 0.05, 0.3, 1.1, true);
    splinters(g, 10, 0.5, rng, 0, Z);
    if (mat > 1) rubbleAround(g, 10, 0.55, rng, colors, 0, Z);
    h = 0.32;
  }
  ironBits(0.55);
  return done(c, h);
};

